import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { IconSparkles, IconChevronRight, IconBolt } from '@tabler/icons-react';
import { db, functions, storage } from '../firebase';
import { getStyleBaseName, groupTemplatesByStyle, pickTemplateForDimension } from './StylePicker';
import stravaConnectButton from '../assets/strava/btn_strava_connect_with_orange.svg';
import './StyleAhaCard.css';

// The landing-page "aha": take the rider's longest recent activity, render a Portrait 3D
// map server-side, run it through the real art pipeline, and reveal a personalised
// before/after. Defaults to the Japanese style for /maps; a host page can feature another
// style (featuredStyleName) and swap the logged-out mock imagery (mockBeforeUrl /
// mockAfterUrls), which is how the event landing page co-brands it. Returning riders who
// already have a map see it instantly (no regeneration). Logged-out visitors get a static
// mock-up mirroring the art-feed onboarding modal.

// Mirror DEFAULT_3D_RENDERER_OPTIONS / portrait dimensions from CreatePage so the backend
// renderer produces the same Portrait 3D capture the Create flow does.
const DEFAULT_3D_MAP_STYLE_URL = 'mapbox://styles/samuelcollin3333/cmf525thz00xg01rk0ewt0i6f';
const DEFAULT_3D_RENDERER_OPTIONS = Object.freeze({
  style: DEFAULT_3D_MAP_STYLE_URL,
  lineWidth: 5,
  coverageRewardMultiplier: 5,
  footprintRewardMultiplier: 1,
  autoCameraSearch: true,
  cameraSearchPitches: [60],
  coarseBearingCount: 18,
  refineBearingOffsets: [],
  robustRetryLimit: 3,
  tightenZoomStep: 0.18,
  tightenMaxSteps: 8,
});
const PORTRAIT_RENDER_OPTIONS = Object.freeze({ ...DEFAULT_3D_RENDERER_OPTIONS, width: 1024, height: 1536 });

// Client watchdog so the wait UI cannot spin forever even if the backend stalls. The
// backend renders the 3D map (RunPod) AND the art in one pipeline, so allow generous time.
const WATCHDOG_MS = 6 * 60 * 1000;
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
const CAROUSEL_INTERVAL_MS = 2500;

// Persist the in-flight generation so a refresh resumes the reveal instead of regenerating.
const LS_REQUEST_ID = 'mythicmaps:styleAha:requestId';

// Logged-out mock-up assets (shared with the art-feed onboarding modal). Lead with the
// Japanese map since the Styles page is Japanese-first.
const ONBOARDING_ASSET = (file) =>
  `https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2Fonboarding_modal%2F${file}?alt=media`;
const MOCK_BEFORE_URL = ONBOARDING_ASSET('OriginalMap3D.png');
const MOCK_AFTER_URLS = [
  ONBOARDING_ASSET('Japanese3D.png'),
  ONBOARDING_ASSET('StardustBrutalism3D.png'),
  ONBOARDING_ASSET('PocketAdventure3D.png'),
  ONBOARDING_ASSET('MeadowWalk3D.png'),
];

// Fallback carousel so the wait UI always rotates real style art even if the live
// template list is slow to load or empty (uses the same reliable onboarding assets).
const FALLBACK_CAROUSEL = [
  { name: 'Stardust Brutalism', imageUrl: ONBOARDING_ASSET('StardustBrutalism3D.png') },
  { name: 'Pocket Adventure', imageUrl: ONBOARDING_ASSET('PocketAdventure3D.png') },
  { name: 'Meadow Walk', imageUrl: ONBOARDING_ASSET('MeadowWalk3D.png') },
];

const lsGet = (key) => {
  try { return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null; } catch (e) { return null; }
};
const lsSet = (key, value) => {
  try { if (typeof window !== 'undefined') window.localStorage.setItem(key, value); } catch (e) { /* ignore */ }
};
const lsRemove = (key) => {
  try { if (typeof window !== 'undefined') window.localStorage.removeItem(key); } catch (e) { /* ignore */ }
};

// Pick the featured style's template from already-grouped styles: prefer the exact name
// match, then any group whose name mentions it, then any 3D template at all. Defaults to
// Japanese (the /maps payoff style).
function pickFeaturedTemplateFromGroups(groups, featuredName = 'japanese') {
  if (!Array.isArray(groups) || groups.length === 0) return null;
  const needle = (featuredName || 'japanese').trim().toLowerCase();
  const named = groups.filter((g) => (g.name || '').toLowerCase().includes(needle));
  const exact = named.find((g) => (g.name || '').trim().toLowerCase() === needle);
  const group = exact || named[0];
  if (group) {
    const tpl = pickTemplateForDimension(group, true);
    if (tpl) return tpl;
  }
  for (const g of groups) {
    const tpl = (g.templates || []).find((t) => t.mapInputType === '3d' || (t.name || '').includes('3D'));
    if (tpl) return tpl;
  }
  return null;
}

// Last-resort pick from a flat template list (only if the grouped styles prop is somehow
// unavailable at request time).
function pickFeaturedTemplateFromTemplates(templates, featuredName = 'japanese') {
  if (!Array.isArray(templates) || templates.length === 0) return null;
  return pickFeaturedTemplateFromGroups(groupTemplatesByStyle(templates), featuredName);
}

// Every available style + one example image, for the wait carousel. Built from the page's
// already-loaded style groups so it covers all styles. Excludes the featured style (the
// reveal payoff) and Zwift Map (the indoor-fix style, not an art style).
function carouselFromGroups(groups, excludeName = 'japanese') {
  if (!Array.isArray(groups)) return [];
  const excludeNeedle = (excludeName || 'japanese').trim().toLowerCase();
  return groups
    .filter((g) => {
      const name = (g.name || '').toLowerCase();
      return !name.includes(excludeNeedle) && !name.includes('zwift');
    })
    .map((g) => ({
      name: g.name,
      // Prefer the purpose-made portrait art (fills the tall carousel frame), falling back
      // to the landscape example for styles that don't have a portrait uploaded yet.
      imageUrl:
        g.portraitExampleImageUrl3D ||
        g.portraitExampleImageUrl ||
        g.exampleImageUrl3D ||
        g.exampleImageUrl ||
        g.exampleImageUrl2D ||
        null,
    }))
    .filter((item) => item.imageUrl);
}

// An activity has GPS data only if it carries a non-empty route polyline we can render.
// This excludes indoor/manual entries (no trace) and the generation code consumes the
// same summary_polyline, so detection and rendering stay aligned.
function hasGpsTrace(a) {
  const line = a?.map?.summary_polyline;
  return typeof line === 'string' && line.trim().length > 0;
}

// Longest activity of ANY type WITH GPS data in the last 90 days.
function pickLongestRecentActivity(activities) {
  if (!Array.isArray(activities)) return null;
  const cutoff = Date.now() - NINETY_DAYS_MS;
  const eligible = activities.filter((a) => {
    if (!hasGpsTrace(a)) return false;
    if (!a.start_date) return false;
    const started = new Date(a.start_date).getTime();
    return Number.isFinite(started) && started >= cutoff;
  });
  if (eligible.length === 0) return null;
  return eligible.reduce((best, a) => ((a.distance || 0) > (best.distance || 0) ? a : best), eligible[0]);
}

const StyleAhaCard = ({
  user,
  onStravaConnect,
  styleGroups = [],
  featuredStyleName = null,
  mockBeforeUrl = MOCK_BEFORE_URL,
  mockAfterUrls = MOCK_AFTER_URLS,
  analyticsSource = 'styles_aha',
  analyticsProps = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const posthog = usePostHog();

  // The carousel and the featured-style pick both come from the page's already-loaded
  // style groups, so the card never depends on a second template fetch. Refs keep the
  // one-shot init effect reading the latest props without widening its dependency array.
  const styleGroupsRef = useRef(styleGroups);
  styleGroupsRef.current = styleGroups;
  const featuredStyleNameRef = useRef(featuredStyleName);
  featuredStyleNameRef.current = featuredStyleName;
  const analyticsPropsRef = useRef(analyticsProps);
  analyticsPropsRef.current = analyticsProps;
  const liveCarousel = useMemo(
    () => carouselFromGroups(styleGroups, featuredStyleName || 'japanese'),
    [styleGroups, featuredStyleName]
  );

  const [phase, setPhase] = useState(user ? 'loading' : 'mock'); // mock | loading | existing | generating | reveal | no-activity | error
  const [beforeUrl, setBeforeUrl] = useState(null);
  const [afterUrl, setAfterUrl] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  // Style + dimension of the generation started this session (drives the reveal copy).
  const [revealStyleName, setRevealStyleName] = useState(null);
  const [generatedIs3D, setGeneratedIs3D] = useState(true);
  // The generation doc we're watching. Driving the onSnapshot from state (not an imperative
  // ref) lets React own subscribe/unsubscribe, so StrictMode's dev remount re-subscribes
  // instead of leaving a dead listener (which made resumed/returning loads loop forever).
  const [watchedRequestId, setWatchedRequestId] = useState(null);
  // The finished generation the commit button acts on (survives the watch ending).
  const [commitRequestId, setCommitRequestId] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [commitError, setCommitError] = useState('');
  // Effective art auto-upload state (explicit field wins, legacy fallback
  // otherwise): when already on, the commit CTA becomes a manage link.
  const [artAlreadyOn, setArtAlreadyOn] = useState(false);

  const loadStartedRef = useRef(false);
  const viewedRef = useRef(false);
  // True except after a real unmount. Used instead of an effect-cleanup "cancelled" flag so
  // the long generation is NOT aborted by React StrictMode's dev mount/unmount/remount or by
  // effect re-runs; only a genuine navigation-away stops it.
  const mountedRef = useRef(true);

  // Resolve a Firebase Storage path to a download URL, swallowing errors.
  const resolvePath = useCallback(async (path) => {
    if (!path) return null;
    try { return await getDownloadURL(ref(storage, path)); } catch (e) { return null; }
  }, []);

  // Begin watching a generation. Only sets state; the effect below owns the actual
  // subscription + watchdog.
  const startWatching = useCallback((requestId) => {
    setWatchedRequestId(requestId);
  }, []);

  // Subscription effect: React owns the onSnapshot + watchdog for whatever we're watching, so
  // StrictMode's dev remount tears down and re-establishes them symmetrically (no dead
  // listener), and a real unmount cleans up. Clearing watchedRequestId stops the watch.
  useEffect(() => {
    if (!watchedRequestId) return undefined;
    const requestId = watchedRequestId;
    const docRef = doc(db, 'GenerationRequests', requestId);
    const unsub = onSnapshot(docRef, async (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      const isComplete = data.status === 'openai_processing_complete' && !data.error;
      if (data.error) {
        setErrorMessage(t('styleAha.errorBody', 'We could not finish your map just now. You can create one yourself in a couple of taps.'));
        setPhase('error');
        posthog?.capture('style_aha_error', { stage: 'generation', request_id: requestId, ...(analyticsPropsRef.current || {}) });
        setWatchedRequestId(null);
        return;
      }
      if (isComplete && data.generatedImagePath) {
        const genUrl = await resolvePath(data.generatedImagePath);
        if (!genUrl) return;
        setAfterUrl(genUrl);
        if (data.originalImagePath) {
          const origUrl = await resolvePath(data.originalImagePath);
          if (origUrl) setBeforeUrl((prev) => prev || origUrl);
        }
        setCommitRequestId(requestId);
        setPhase('reveal');
        posthog?.capture('style_aha_rendered', { request_id: requestId, ...(analyticsPropsRef.current || {}) });
        setWatchedRequestId(null);
      }
    });
    const watchdog = setTimeout(() => {
      setPhase((prev) => (prev === 'reveal' ? prev : 'error'));
      setErrorMessage(t('styleAha.timeoutBody', 'Your map is taking longer than usual. It will appear on your profile when it is ready, or you can start a fresh one now.'));
      posthog?.capture('style_aha_error', { stage: 'watchdog', ...(analyticsPropsRef.current || {}) });
      setWatchedRequestId(null);
    }, WATCHDOG_MS);
    return () => { unsub(); clearTimeout(watchdog); };
  }, [watchedRequestId, resolvePath, posthog, t]);

  // Art auto-upload state drives which CTA the reveal shows.
  useEffect(() => {
    if (!user) return;
    let stale = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        const auto = snap.data()?.settings?.autoUpload || {};
        const artOn = typeof auto.artEnabled === 'boolean'
          ? auto.artEnabled
          : (auto.enabled === true && auto.zwiftOnly !== true);
        if (!stale) setArtAlreadyOn(artOn);
      } catch (err) {
        console.warn('[StyleAhaCard] auto-upload state read failed:', err);
      }
    })();
    return () => { stale = true; };
  }, [user]);

  // ----- One-shot init for logged-in users -----
  useEffect(() => {
    if (!user || loadStartedRef.current) return;
    loadStartedRef.current = true;

    (async () => {
      try {
        // The carousel is sourced from the page's style groups (prop), so no fetch here.

        // Featured-style hosts (the event page) use their own resume slot: a /maps
        // generation must never hijack the featured reveal, and vice versa. The slot also
        // makes the featured generation once-per-browser: revisits resume the finished
        // doc instead of spending another diamond.
        const featuredNow = featuredStyleNameRef.current || null;
        const lsKey = featuredNow ? `${LS_REQUEST_ID}:${featuredNow}` : LS_REQUEST_ID;

        // 2) Resume an in-flight generation from a previous visit (refresh-safe). The
        //    before/after both come from the watched doc, so nothing else to restore.
        //    A featured slot only ever holds a generation of that style, so the styled
        //    reveal title is correct on resume too.
        const savedRequestId = lsGet(lsKey);
        if (savedRequestId) {
          if (featuredNow) setRevealStyleName(featuredNow);
          setPhase('generating');
          startWatching(savedRequestId);
          posthog?.capture('style_aha_resumed', { request_id: savedRequestId, ...(analyticsPropsRef.current || {}) });
          return;
        }

        // 3) Returning user: if they already have a finished map, show it instead of
        //    generating a new one. This is what stops subsequent page loads from kicking off
        //    a fresh generation. Mirrors ProfilePage's own-profile fetch: getUserPublicMaps
        //    with includePrivate, which (since the requester is the owner) drops the isPublic
        //    filter server-side and returns PRIVATE maps too. No diamond beat here.
        //    Skipped in featured mode: an old map in some other style would defeat the
        //    event showcase, which exists to render THIS style.
        if (!featuredNow) {
          try {
            const getUserMaps = httpsCallable(functions, 'getUserPublicMaps');
            const mapsResult = await getUserMaps({ userId: user.uid, includePrivate: true, limit: 1 });
            if (!mountedRef.current) return;
            const maps = Array.isArray(mapsResult.data?.maps) ? mapsResult.data.maps : [];
            const latest = maps.find((m) => m.generatedImagePath);
            if (latest) {
              const genUrl = await resolvePath(latest.generatedImagePath);
              if (!mountedRef.current) return;
              if (genUrl) {
                setAfterUrl(genUrl);
                if (latest.originalImagePath) {
                  const origUrl = await resolvePath(latest.originalImagePath);
                  if (mountedRef.current && origUrl) setBeforeUrl(origUrl);
                }
                setCommitRequestId(latest.id || null);
                setPhase('existing');
                posthog?.capture('style_aha_existing', { map_id: latest.id || null, ...(analyticsPropsRef.current || {}) });
                return;
              }
            }
          } catch (mapsErr) {
            console.warn('[StyleAhaCard] existing-maps lookup failed:', mapsErr);
          }
        }
        if (!mountedRef.current) return;

        // 4) New user: find the longest recent activity and generate.
        const getActivities = httpsCallable(functions, 'getStravaActivities');
        const actResult = await getActivities();
        if (!mountedRef.current) return;
        const activities = Array.isArray(actResult?.data) ? actResult.data : [];
        const activity = pickLongestRecentActivity(activities);
        if (!activity) {
          setPhase('no-activity');
          posthog?.capture('style_aha_no_activity', { activity_count: activities.length, ...(analyticsPropsRef.current || {}) });
          return;
        }

        // Pick the featured style's template from the page's loaded style groups; only if
        // those are somehow not ready do we fetch the template list directly as a fallback.
        const featuredName = featuredStyleNameRef.current || 'japanese';
        let featuredTemplate = pickFeaturedTemplateFromGroups(styleGroupsRef.current, featuredName);
        if (!featuredTemplate) {
          try {
            const getTemplates = httpsCallable(functions, 'getPromptTemplates');
            const tplResult = await getTemplates();
            if (!mountedRef.current) return;
            const templates = Array.isArray(tplResult.data?.templates)
              ? tplResult.data.templates.filter((tpl) => tpl.enabled !== false && !tpl.adminOnly)
              : [];
            featuredTemplate = pickFeaturedTemplateFromTemplates(templates, featuredName);
          } catch (tplErr) {
            console.warn('[StyleAhaCard] template fallback fetch failed:', tplErr);
          }
        }
        if (!featuredTemplate) {
          setErrorMessage(t('styleAha.errorBody', 'We could not finish your map just now. You can create one yourself in a couple of taps.'));
          setPhase('error');
          posthog?.capture('style_aha_error', { stage: 'no_template', ...(analyticsPropsRef.current || {}) });
          return;
        }

        // Begin the process. The grant happens once we have the requestId (a sub-second call,
        // so it still lands at the very start of the wait) so it can be keyed to that id.
        setPhase('generating');
        posthog?.capture('style_aha_generation_started', { activity_id: String(activity.id), ...(analyticsPropsRef.current || {}) });

        // Remember what we are generating so the reveal copy names the real style.
        setRevealStyleName(getStyleBaseName(featuredTemplate.name) || null);
        const tplIs3D = featuredTemplate.mapInputType === '3d' || (featuredTemplate.name || '').includes('3D');
        setGeneratedIs3D(tplIs3D);

        // Request a Portrait generation. 3D: the backend renders the 3D map (RunPod) and
        // the art in one pipeline; the plain render comes back as originalImagePath (the
        // "before"), the art as generatedImagePath (the "after"). A 2D-only featured style
        // takes the standard server-side 2D path instead. No challenge-only flags.
        const requestImageGeneration = httpsCallable(functions, 'requestImageGeneration');
        const genResult = await requestImageGeneration(tplIs3D ? {
          StravaId: String(activity.id),
          promptTemplateId: featuredTemplate.id,
          gpxDataSource: 'polyline',
          mapInputType: '3d',
          mapOrientation: 'portrait',
          use3DMap: true,
          threeDRenderSource: 'renderer',
          renderer3DRenderOptions: PORTRAIT_RENDER_OPTIONS,
        } : {
          StravaId: String(activity.id),
          promptTemplateId: featuredTemplate.id,
          gpxDataSource: 'polyline',
          mapInputType: '2d',
          mapOrientation: 'portrait',
        });
        if (!mountedRef.current) return;
        const requestId = genResult?.data?.requestId;
        if (!requestId) throw new Error('Generation did not return a request id.');
        lsSet(lsKey, requestId);

        // Watch the doc until complete, then reveal.
        startWatching(requestId);
      } catch (err) {
        if (!mountedRef.current) return;
        console.error('[StyleAhaCard] init failed:', err);
        setWatchedRequestId(null);
        setErrorMessage(t('styleAha.errorBody', 'We could not finish your map just now. You can create one yourself in a couple of taps.'));
        setPhase('error');
        posthog?.capture('style_aha_error', { stage: 'init', message: err?.message || null, ...(analyticsPropsRef.current || {}) });
      }
    })();
    // No cleanup that aborts the flow: the generation must survive StrictMode's dev
    // remount and any re-render. Real-unmount teardown is handled via mountedRef below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // View event (once).
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    posthog?.capture('style_aha_viewed', { is_authenticated: !!user, ...(analyticsPropsRef.current || {}) });
  }, [posthog, user]);

  // Rotate the carousel during the wait, and the mock "after" image when logged out.
  // Hosts passing mockAfterUrls must memoize the array (a single-item list simply stays
  // static: the interval is skipped and index 0 remains visible).
  useEffect(() => {
    const items = phase === 'mock'
      ? mockAfterUrls
      : (liveCarousel.length > 0 ? liveCarousel : FALLBACK_CAROUSEL);
    if (!['mock', 'loading', 'generating'].includes(phase) || items.length <= 1) return undefined;
    const id = setInterval(() => {
      setCarouselIndex((i) => (i + 1) % items.length);
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [phase, liveCarousel, mockAfterUrls]);

  // Mark mounted on (re)mount; flip false only on a real unmount. The snapshot + watchdog are
  // owned by the subscription effect above (so they're StrictMode-safe).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const goCreate = useCallback(() => {
    posthog?.capture('style_aha_cta', { destination: 'create', ...(analyticsPropsRef.current || {}) });
    const featuredNow = featuredStyleNameRef.current || null;
    lsRemove(featuredNow ? `${LS_REQUEST_ID}:${featuredNow}` : LS_REQUEST_ID);
    navigate('/create');
  }, [navigate, posthog]);

  // Mirrors the /fix aha commit, art-side: turn on art auto-uploads for every
  // future outdoor ride AND put this map on the ride's Strava activity.
  const handleCommit = useCallback(async () => {
    if (!user || !commitRequestId || committing || committed) return;

    setCommitting(true);
    setCommitError('');

    try {
      // 1) Enable art auto-uploads, preserving an explicit indoor-fix choice.
      // Docs without one get it pinned off so the legacy fallback
      // (enabled === true) doesn't switch the indoor fix on as a side effect.
      const userSnap = await getDoc(doc(db, 'users', user.uid));
      const auto = userSnap.data()?.settings?.autoUpload || {};
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.autoUpload.artEnabled': true,
        ...(typeof auto.indoorFixEnabled === 'boolean' ? {} : { 'settings.autoUpload.indoorFixEnabled': false }),
        // Mirror the legacy master for anything still reading it.
        'settings.autoUpload.enabled': true,
      });
      posthog?.capture('style_aha_auto_upload_enabled', { source: analyticsSource, ...(analyticsPropsRef.current || {}) });

      // 2) Put this map on the Strava activity (queued server-side).
      const uploadMapToStravaMedia = httpsCallable(functions, 'uploadMapToStravaMedia');
      await uploadMapToStravaMedia({
        requestId: commitRequestId,
        includeGenerated: true,
        includeVideo: true,
        includeOriginal: true,
      });

      setCommitted(true);
      posthog?.capture('style_aha_committed', { request_id: commitRequestId, ...(analyticsPropsRef.current || {}) });
    } catch (err) {
      console.error('[StyleAhaCard] commit failed:', err);
      setCommitError(t('styleAha.commitError', 'Something went wrong turning on auto-upload. Please try again.'));
    } finally {
      setCommitting(false);
    }
  }, [user, commitRequestId, committing, committed, posthog, t, analyticsSource]);

  // ---------- Render ----------

  const header = (title) => (
    <div className="style-aha-header">
      <span className="style-aha-eyebrow">
        <IconSparkles size={16} />
        {t('styleAha.eyebrow', 'Your activity, as art')}
      </span>
      <h2>{title}</h2>
    </div>
  );

  // Logged-out mock-up: static before, rotating art "after". A host featuring one style
  // (the event/invite landings) names it in the headline so the mock reads as that style.
  if (phase === 'mock') {
    return (
      <div className="style-aha-card style-aha-mock">
        {header(featuredStyleName
          ? t('styleAha.mockTitleStyled', { defaultValue: 'Turn your next activity into a {{style}} art map, instantly.', style: featuredStyleName })
          : t('styleAha.mockTitle', 'Turn your next activity into art, instantly.'))}
        <div className="style-aha-beforeafter">
          <div className="style-aha-panel">
            <span className="style-aha-panel-label style-aha-panel-label-before">
              {t('styleAha.beforeLabel', 'Your activity')}
            </span>
            <div className="style-aha-map style-aha-map-before">
              <img src={mockBeforeUrl} alt={t('styleAha.beforeAlt', 'Your activity map')} />
            </div>
          </div>
          <div className="style-aha-arrow" aria-hidden="true">→</div>
          <div className="style-aha-panel">
            <span className="style-aha-panel-label style-aha-panel-label-after">
              {t('styleAha.afterLabel', 'Your mythic map')}
            </span>
            <div className="style-aha-map style-aha-map-after">
              {mockAfterUrls.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={t('styleAha.afterAlt', 'Your activity as a mythic map')}
                  className={i === carouselIndex % mockAfterUrls.length ? 'visible' : ''}
                />
              ))}
            </div>
          </div>
        </div>
        <p className="style-aha-connect-hook">
          {t('styleAha.mockConnectHook', 'Connect Strava once and we turn your latest activity into art on the spot, for free. No uploads, no GPX files.')}
        </p>
        <button type="button" className="style-aha-strava-cta" onClick={onStravaConnect}>
          <img src={stravaConnectButton} alt={t('styleAha.connect', 'Connect with Strava, it is free')} />
        </button>
      </div>
    );
  }

  if (phase === 'loading' || phase === 'generating') {
    const carouselItems = liveCarousel.length > 0 ? liveCarousel : FALLBACK_CAROUSEL;
    const current = carouselItems[carouselIndex % carouselItems.length];
    return (
      <div className="style-aha-card">
        {header(t('styleAha.generatingTitle', 'Turning your route into art...'))}
        <div className="style-aha-carousel">
          <div className="style-aha-carousel-frame">
            <img src={current.imageUrl} alt={current.name} />
            <span className="style-aha-carousel-caption">
              {t('styleAha.carouselCaption', { defaultValue: 'Trying on every style while you wait: {{name}}', name: current.name })}
            </span>
          </div>
        </div>
        <p className="style-aha-wait-note">
          {t('styleAha.waitNote', 'Rendering your real route in 3D, then painting it. This usually takes a minute.')}
        </p>
      </div>
    );
  }

  if (phase === 'existing' || phase === 'reveal') {
    const isReveal = phase === 'reveal';
    return (
      <div className="style-aha-card">
        {header(isReveal
          ? (featuredStyleName
            ? t('styleAha.revealTitleStyled', { defaultValue: 'Here is your activity, as a {{style}} art map.', style: revealStyleName || featuredStyleName })
            : t('styleAha.revealTitle', 'Here is your activity, as a Japanese art map.'))
          : t('styleAha.existingTitle', 'Your latest mythic map.'))}
        <div className="style-aha-beforeafter">
          {beforeUrl && (
            <>
              <div className="style-aha-panel">
                <span className="style-aha-panel-label style-aha-panel-label-before">
                  {generatedIs3D
                    ? t('styleAha.beforeRouteLabel', 'Your route in 3D')
                    : t('styleAha.beforeRouteLabel2d', 'Your route')}
                </span>
                <div className="style-aha-map style-aha-map-before">
                  <img src={beforeUrl} alt={t('styleAha.beforeRouteAlt', 'Your route rendered in 3D')} />
                </div>
              </div>
              <div className="style-aha-arrow" aria-hidden="true">→</div>
            </>
          )}
          <div className="style-aha-panel">
            <span className="style-aha-panel-label style-aha-panel-label-after">
              {t('styleAha.afterLabel', 'Your mythic map')}
            </span>
            <div className="style-aha-map style-aha-map-after">
              {afterUrl && <img src={afterUrl} alt={t('styleAha.afterAlt', 'Your activity as a mythic map')} />}
            </div>
          </div>
        </div>
        <div className="style-aha-actions">
          {committed ? (
            <div className="style-aha-commit-done">
              <p>
                {t('styleAha.commitDone', '✓ Added to your Strava. Every future outdoor ride now gets a map automatically.')}
              </p>
              <button type="button" className="style-aha-secondary-link" onClick={() => navigate('/settings#art-auto-upload')}>
                {t('styleAha.commitManage', 'Manage auto-upload settings')}
                <IconChevronRight size={16} />
              </button>
            </div>
          ) : artAlreadyOn ? (
            <button
              type="button"
              className="challenge-cta-button"
              onClick={() => {
                posthog?.capture('style_aha_cta', { destination: 'settings', ...(analyticsPropsRef.current || {}) });
                navigate('/settings#art-auto-upload');
              }}
            >
              {t('styleAha.commitManage', 'Manage auto-upload settings')}
              <IconChevronRight size={18} />
            </button>
          ) : commitRequestId ? (
            <>
              <button
                type="button"
                className="challenge-cta-button"
                onClick={handleCommit}
                disabled={committing}
              >
                <IconBolt size={18} />
                {committing
                  ? t('styleAha.committing', 'Adding to your Strava…')
                  : t('styleAha.commitCta', 'Add to my Strava + auto-upload every future ride')}
              </button>
              <p className="style-aha-commit-note">
                {t('styleAha.commitNote', 'Free. We only add photos to the activity, never your route, time, or stats. Turn it off anytime in two clicks.')}
              </p>
              {commitError && <p className="style-aha-commit-error">{commitError}</p>}
            </>
          ) : (
            <button type="button" className="challenge-cta-button" onClick={goCreate}>
              {t('styleAha.makeAnother', 'Make another')}
              <IconChevronRight size={18} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // no-activity / error
  return (
    <div className="style-aha-card">
      {header(phase === 'no-activity'
        ? t('styleAha.noActivityTitle', 'No recent activity to turn into art yet.')
        : t('styleAha.errorTitle', 'Let us make your first mythic map.'))}
      <div className="style-aha-empty">
        <p>
          {phase === 'no-activity'
            ? t('styleAha.noActivityBody', 'We could not find a recent route to use. Pick any activity and we will turn it into a 3D art map.')
            : errorMessage || t('styleAha.errorBody', 'We could not finish your map just now. You can create one yourself in a couple of taps.')}
        </p>
      </div>
      <div className="style-aha-actions">
        <button type="button" className="challenge-cta-button" onClick={goCreate}>
          <IconBolt size={18} />
          {t('styleAha.createCta', 'Create a map')}
        </button>
      </div>
    </div>
  );
};

export default StyleAhaCard;
